import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChevronRight, Users, Activity, Eye } from "lucide-react";
import { formatDateTime } from "../lib/format";

export default function TeamProductivity() {
  const { user } = useAuth();
  const [tls, setTls] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [filterTl, setFilterTl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (user?.role === "super_custodian" || user?.role === "hod") {
          const [tRes, rRes] = await Promise.all([
            api.get("/productivity/tls"),
            api.get("/productivity/recruiters", { params: filterTl ? { tl_email: filterTl } : {} }),
          ]);
          setTls(tRes.data);
          setRecruiters(rRes.data);
        } else {
          const { data } = await api.get("/productivity/recruiters");
          setRecruiters(data);
        }
      } finally { setLoading(false); }
    })();
  }, [user?.role, filterTl]);

  const showTlTable = (user?.role === "super_custodian" || user?.role === "hod") && !filterTl;

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900">Team productivity</h1>
        <p className="text-slate-500 mt-1">
          {user?.role === "super_custodian"
            ? "Organisation-wide recruiter activity, grouped by TL."
            : user?.role === "hod"
            ? "Recruiter activity across your TL teams."
            : "Activity for recruiters mapped to you."}
        </p>
      </div>

      {showTlTable && (
        <Card className="p-0 border-slate-200 overflow-hidden mb-6" data-testid="tl-summary-table">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-lg text-slate-900">Team leads</h3>
              <p className="text-xs text-slate-500">{tls.length} TLs</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-3 px-6 font-semibold">TL</th>
                  <th className="text-right py-3 px-6 font-semibold">Team size</th>
                  <th className="text-right py-3 px-6 font-semibold">Total candidates</th>
                  <th className="text-right py-3 px-6 font-semibold">Added this week</th>
                  <th className="text-right py-3 px-6 font-semibold">Updates this week</th>
                  <th className="py-3 px-6"></th>
                </tr>
              </thead>
              <tbody>
                {tls.map((t) => (
                  <tr key={t.email} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tl-row-${t.email}`}>
                    <td className="py-3 px-6">
                      <div className="font-medium text-slate-900">{t.name || t.email}</div>
                      <div className="text-xs text-slate-500">{t.email}</div>
                    </td>
                    <td className="py-3 px-6 text-right text-slate-700">{t.team_size}</td>
                    <td className="py-3 px-6 text-right text-slate-700">{t.total_team_candidates}</td>
                    <td className="py-3 px-6 text-right text-emerald-600 font-medium">+{t.team_added_this_week}</td>
                    <td className="py-3 px-6 text-right text-indigo-600 font-medium">{t.team_updates_this_week}</td>
                    <td className="py-3 px-6 text-right">
                      <Button data-testid={`view-team-${t.email}`} size="sm" variant="outline" onClick={() => setFilterTl(t.email)}>
                        <Users className="w-3.5 h-3.5 mr-1.5" />View team
                      </Button>
                    </td>
                  </tr>
                ))}
                {tls.length === 0 && <tr><td colSpan="6" className="py-6 px-6 text-center text-slate-400 text-sm">No TLs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-0 border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">Recruiters {filterTl && <span className="text-sm text-slate-500 font-normal">— under {filterTl}</span>}</h3>
            <p className="text-xs text-slate-500">{recruiters.length} recruiters</p>
          </div>
          {filterTl && (
            <Button data-testid="back-to-tls" size="sm" variant="outline" onClick={() => setFilterTl(null)}>← All TLs</Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left py-3 px-6 font-semibold">Recruiter</th>
                <th className="text-right py-3 px-6 font-semibold">Added this week</th>
                <th className="text-right py-3 px-6 font-semibold">Updates this week</th>
                <th className="text-left py-3 px-6 font-semibold">Last sync</th>
                <th className="text-right py-3 px-6 font-semibold">Total candidates</th>
                <th className="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="py-6 px-6 text-center text-slate-400 text-sm">Loading…</td></tr>}
              {!loading && recruiters.map((r) => (
                <tr key={r.email} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`recruiter-row-${r.email}`}>
                  <td className="py-3 px-6">
                    <div className="font-medium text-slate-900">{r.name || r.email}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="py-3 px-6 text-right text-emerald-600 font-medium">+{r.added_this_week}</td>
                  <td className="py-3 px-6 text-right text-indigo-600 font-medium">{r.updates_this_week}</td>
                  <td className="py-3 px-6 text-xs text-slate-600">{r.last_sync_ts ? formatDateTime(r.last_sync_ts) : "—"}</td>
                  <td className="py-3 px-6 text-right text-slate-700">{r.total_candidates}</td>
                  <td className="py-3 px-6 text-right">
                    <Link to={`/productivity/recruiter/${encodeURIComponent(r.email)}`}>
                      <Button data-testid={`view-more-${r.email}`} size="sm" variant="outline">
                        <Eye className="w-3.5 h-3.5 mr-1.5" />View more
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && recruiters.length === 0 && <tr><td colSpan="6" className="py-6 px-6 text-center text-slate-400 text-sm">No recruiters in scope.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
